
import { Component, Prop, Vue } from 'vue-property-decorator';

import axios, { AxiosResponse } from 'axios';
import sortBy from 'lodash/sortBy';
import {
  email,
  helpers,
  minLength,
  required,
  requiredIf,
  requiredUnless,
  sameAs,
} from 'vuelidate/lib/validators';

import {
  ApplicationModule,
  Company,
  CompanyApplicationModule,
  Industry,
} from '@/lib/models';
import { unitedStatesGeographicalRegions } from '@/lib/geo';

@Component({
  validations: {
    form: {
      isNewCompany: {
        required,
      },
      active: {
        required,
      },
      dataRetentionExpirationDate: {
        requiredUnless: requiredUnless('active'),
      },
      companyName: {
        required,
        validCompanyName: helpers.regex('validCompanyName', /^[a-zA-Z0-9 \.\,\(\)\-\_\&]*$/),
      },
      industry: {
        required,
      },
      country: {
        required,
      },
      postalCode: {
        required,
      },
      city: {
        required,
      },
      geographicalRegion: {
        required,
      },
      address1: {
        required,
      },
      adminEmail: {
        requiredIf: requiredIf('isNewCompany'),
        email,
      },
      adminEmailConfirmation: {
        requiredIf: requiredIf('isNewCompany'),
        email,
        sameAsAdminEmail: sameAs('adminEmail'),
      },
      adminFirstName: {
        requiredIf: requiredIf('isNewCompany'),
      },
      adminLastName: {
        requiredIf: requiredIf('isNewCompany'),
      },
      adminPhone: {
        requiredIf: requiredIf('isNewCompany'),
      },
      adminTitle: {
        requiredIf: requiredIf('isNewCompany'),
      },
      selectedApplicationModuleIds: {
        requiredIf: requiredIf('isNewCompany'),
        minLength: minLength(1),
      },
    },
  },
})
export default class CreateEditCompanyForm extends Vue {
  @Prop({ type: Object, required: false }) private readonly company!: Company;
  @Prop({ type: Boolean, required: true }) private readonly submitting!: boolean;

  private applicationModules: ApplicationModule[] = [];
  private industries: Industry[] = [];
  private countryOptions = [
    { value: 'US', text: 'United States' },
    { value: 'Other', text: 'Other (specify as part of the City field)' },
  ];
  private vmHostIdentificationMethodOptions = [
    { text: 'IP (Default)', value: 'ip' },
    { text: 'DNS', value: 'dns' },
  ];

  private applicationModuleIdToAdd: string = '';
  private applicationModuleIdToRemove: string = '';

  // TODO: Extract the fields from this object into an interface so we can
  // actually get type checking (just like we do in the Vuln Manager app)
  private form: any = {
    isNewCompany: this.company === undefined,
    active: true,
    dataRetentionExpirationDate: null,
    companyName: null,
    industry: null,
    country: null,
    postalCode: null,
    city: null,
    geographicalRegion: null,
    address1: null,
    address2: null,
    adminEmail: null,
    adminEmailConfirmation: null,
    adminFirstName: null,
    adminLastName: null,
    adminPhone: null,
    adminTitle: null,
    selectedApplicationModuleIds: [],
    sosVideoClientId: null,
    tracephishingFreeTrial: null,
    vmQualysClientId: null,
    vmHostIdentificationMethod: null,
    vmQualysNetworkId: null,
    traceeducationCanAccessVimeoLibrary: null,
  };

  private created(): void {
    axios.all([this.getApplicationModules(), this.getIndustries()])
      .then(axios.spread((applicationModulesResponse, industriesResponse) => {
        this.applicationModules = applicationModulesResponse.data.data;
        this.industries = industriesResponse.data.data;
      }))
      // When editing a company, preselect the country for that company
      // When creating a company, preselect the US
      .then(() => {
        if (this.company) {
          if (this.company.country === 'US') {
            this.form.country = 'US';
          } else {
            this.form.country = 'Other';
          }
        } else {
          this.form.country = 'US';
        }
      })
      // When editing a company, preselect the geographical region for that company
      .then(() => {
        if (this.company) {
          if (this.company.country === 'US') {
            this.form.geographicalRegion = this.company.geographicalRegion;
          } else {
            this.form.geographicalRegion = 'Other';
          }
        }
      })
      // When editing a company, prepopulate the remaining fields and validate the form
      .then(() => {
        if (this.company) {
          this.form.active = this.company.active;
          this.form.dataRetentionExpirationDate = this.company.dataRetentionExpirationDate;
          this.form.companyName = this.company.name;
          this.form.industry = this.company.industry.id;
          this.form.postalCode = this.company.postalCode;
          this.form.city = this.company.city;
          this.form.address1 = this.company.address1;
          this.form.address2 = this.company.address2;
          if (this.company.companyApplicationModules !== null) {
            this.form.selectedApplicationModuleIds = this.company.companyApplicationModules
              .map((cam) => cam.applicationModule.id);
          }
          this.form.sosVideoClientId = this.company.sosVideoClientId;
          this.form.tracephishingFreeTrial = this.company.tracephishingFreeTrial;
          this.form.vmQualysClientId = this.company.vmQualysClientId;
          this.form.vmHostIdentificationMethod = this.company.vmHostIdentificationMethod;
          this.form.vmQualysNetworkId = this.company.vmQualysNetworkId;
          this.form.traceeducationCanAccessVimeoLibrary = this.company.traceeducationCanAccessVimeoLibrary;
          this.$v.form.$touch();
        }
      });
  }

  private async getApplicationModules(): Promise<AxiosResponse> {
    const url: string = `${process.env.VUE_APP_BACKEND_API_HOST}/applicationmodules?sort=name`;
    const accessToken: string = await (this as any).$auth0.getAccessTokenSilently();
    return axios.get(url, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });
  }

  private async getIndustries(): Promise<AxiosResponse> {
    const url: string = `${process.env.VUE_APP_BACKEND_API_HOST}/industries?sort=name`;
    const accessToken: string = await (this as any).$auth0.getAccessTokenSilently();
    return axios.get(url, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });
  }

  private get industryOptions(): any[] {
    return this.industries.map((industry: Industry) => {
      return {
        value: industry.id,
        text: industry.name,
      };
    });
  }

  private get selectedIndustry(): Industry | undefined {
    return this.industries.find((industry) => industry.id === this.form.industry);
  }

  private get geographicalRegionOptions() {
    if (this.form.country === 'US') {
      return unitedStatesGeographicalRegions;
    } else {
      return [ { value: 'Other', text: 'Other (specify as part of the City field)' } ];
    }
  }

  private updateGeographicalRegions(): void {
    if (this.form.country === 'Other') {
      this.form.geographicalRegion = 'Other';
    } else {
      this.form.geographicalRegion = null;
    }
    (this.$v.form.geographicalRegion as any).$touch();
  }

  private get addableApplicationModuleOptions(): any[] {
    return this.applicationModules
      .filter((applicationModule) => !this.form.selectedApplicationModuleIds.includes(applicationModule.id))
      .map((applicationModule) => {
        return {
          value: applicationModule.id,
          text: applicationModule.name,
        };
      });
  }

  private get sortedAddableApplicationModuleOptions(): any[] {
    return sortBy(this.addableApplicationModuleOptions, 'text');
  }

  private addSelectedApplicationModule(): void {
    const applicationModuleIdToAdd = this.applicationModuleIdToAdd;
    this.applicationModuleIdToAdd = '';
    this.form.selectedApplicationModuleIds.push(applicationModuleIdToAdd);
    (this.$v.form.selectedApplicationModuleIds as any).$touch();
  }

  private get selectedApplicationModules(): ApplicationModule[] {
    return this.applicationModules
      .filter((applicationModule) => this.form.selectedApplicationModuleIds.includes(applicationModule.id));
  }

  private get removableApplicationModuleOptions(): any[] {
    return this.applicationModules
      .filter((applicationModule) => this.form.selectedApplicationModuleIds.includes(applicationModule.id))
      .map((applicationModule) => {
        return {
          value: applicationModule.id,
          text: applicationModule.name,
        };
      });
  }

  private get sortedRemovableApplicationModuleOptions(): any[] {
    return sortBy(this.removableApplicationModuleOptions, 'text');
  }

  private removeSelectedApplicationModule(): void {
    this.form.selectedApplicationModuleIds = this.form.selectedApplicationModuleIds
      .filter((applicationModuleId: string) => applicationModuleId !== this.applicationModuleIdToRemove);
  }

  // Begin properties only relevant to existing companies
  private get sortedCompanyApplicationModules(): CompanyApplicationModule[] {
    if (this.company === undefined || this.company.companyApplicationModules === null) {
      return [];
    }
    return sortBy(this.company.companyApplicationModules, 'applicationModule.name');
  }

  private get addedApplicationModules(): ApplicationModule[] {
    if (this.company === undefined || this.company.companyApplicationModules === null) {
      return [];
    }
    return this.company!.companyApplicationModules.map((cam) => cam.applicationModule);
  }

  private get addedApplicationModuleIds(): string[] {
    return this.addedApplicationModules.map((applicationModule) => applicationModule.id!);
  }

  private get addableApplicationModules(): ApplicationModule[] {
    const modules = this.applicationModules
      .filter((applicationModule) => !this.addedApplicationModuleIds.includes(applicationModule.id!));
    return sortBy(modules, 'name');
  }

  private get addableApplicationModuleIds(): string[] {
    return this.addableApplicationModules.map((applicationModule) => applicationModule.id!);
  }

  private onClickFixDBSchema(applicationModule: ApplicationModule): void {
    this.$emit('fixDBSchema', applicationModule);
  }

  private onClickAddModule(applicationModule: ApplicationModule): void {
    this.$bvModal.msgBoxConfirm(`Are you sure you want to add the ${applicationModule.name} module?`, {
      title: 'Confirm Changes',
      okTitle: 'Add Module',
      okVariant: 'primary',
      centered: true,
    }).then((addModule: boolean) => {
      if (addModule) {
        this.$emit('addModule', applicationModule);
      }
    });
  }

  private onClickRemoveModule(applicationModule: ApplicationModule): void {
    this.$bvModal.msgBoxConfirm(`Are you sure you want to remove the ${applicationModule.name} module?`, {
      title: 'Confirm Changes',
      okTitle: 'Remove Module',
      okVariant: 'danger',
      centered: true,
    }).then((removeModule: boolean) => {
      if (removeModule) {
        this.$emit('removeModule', applicationModule);
      }
    });
  }
  // End properties only relevant to existing companies

  private onSubmit(event: Event): void {
    const companyData: Company = {
      name: this.form.companyName,
      address1: this.form.address1,
      address2: this.form.address2,
      address3: '',
      country: this.form.country,
      geographicalRegion: this.form.geographicalRegion,
      city: this.form.city,
      postalCode: this.form.postalCode,
      sosVideoClientId: this.form.sosVideoClientId,
      dataRetentionExpirationDate: this.form.active ? null : this.form.dataRetentionExpirationDate,
      tracephishingFreeTrial: this.form.tracephishingFreeTrial,
      vmQualysClientId: this.form.vmQualysClientId,
      vmHostIdentificationMethod: this.form.vmHostIdentificationMethod,
      vmQualysNetworkId: this.form.vmQualysNetworkId,
      traceeducationCanAccessVimeoLibrary: this.form.traceeducationCanAccessVimeoLibrary,
      requireMfa: false,
      industry: this.selectedIndustry!,
      companyApplicationModules: this.selectedApplicationModules.map((applicationModule) => {
        return {
          applicationModule: {
            name: applicationModule.name,
          },
        };
      }),
      adminUser: {
        firstName: this.form.adminFirstName,
        lastName: this.form.adminLastName,
        title: this.form.adminTitle,
        phone: this.form.adminPhone,
        email: this.form.adminEmail,
        active: true,
        primaryAdmin: true,
        userRoles: [],
        userGroups: [],
      },
      active: this.form.isNewCompany ? true : this.form.active,
      archived: false,
    };
    this.$emit('submit', companyData);
  }
}
